import {
  buildProgramLadder,
  ladderColumnLabel,
  type LadderProgram,
  type ProgramLadder,
  type SubjectAreaCode,
} from '@dosfilos/domain';
import { AREA_COLOR, NO_AREA_COLOR } from './subject-area-colors';

// ─── Program ladder → PDF ────────────────────────────────────────────────────
// Everything the ladder PDF prints, computed here and not in the document: the
// document only lays out what this module decides. Pure, so the pagination
// rules can be tested without rendering a page.
//
// THE PAGINATION RULES (learned the hard way on the syllabus PDF):
//   1. A program's row is atomic (`wrap={false}`) when it fits on a page: it is
//      never cut between pages, it moves whole to the next one.
//   2. `wrap={false}` on a block TALLER than a page does not move it — it
//      overlaps what follows. So a row taller than a page is split HERE, in the
//      data, into chunks that each fit, and every chunk is its own atomic row
//      named "(continuation)".
//   3. The credits-per-year total is glued to the last row, so it never opens a
//      page alone.
// Heights are ESTIMATED, conservatively: a short estimate costs at most a row
// jumping to the next page, never an overlap, because splitting is decided
// against a full page with a safety margin.

export interface LadderPdfCourse {
  id: string;
  code: string;
  name: string;
  credits: number;
  color: string;
}

export interface LadderPdfRow {
  programId: string;
  name: string;
  /** "Año 2 – Año 3". */
  span: string;
  /** The columns the program occupies, to shade its band. */
  startColumn: number;
  termCount: number;
  ownCredits: number;
  /** Cumulative award: its total, and the programs it includes. */
  award?: { credits: number; includes: string[] };
  /** One list per ladder column; empty outside the program's span. */
  cells: LadderPdfCourse[][];
  /** True for every chunk after the first of a row that did not fit a page. */
  continuation: boolean;
}

export interface LadderPdfLabels {
  title: string;
  subtitle: string;
  program: string;
  continuation: string;
  columnTotal: string;
  legend: string;
  notes: string;
  credits: (count: number) => string;
  creditsShort: (n: number) => string;
  award: (count: number, programs: string) => string;
  page: (n: number, total: number) => string;
}

export interface LadderPdfData {
  tenantName: string;
  logoUrl?: string;
  columns: string[];
  rows: LadderPdfRow[];
  columnCredits: number[];
  /** Glue the totals row to the last row (rule 3), unless together they would
   *  not fit a page. */
  glueTotalsToLastRow: boolean;
  legend: { label: string; color: string }[];
  notes: string[];
  labels: LadderPdfLabels;
}

export interface LadderCourseInfo {
  code: string;
  name: string;
  credits: number;
  area?: SubjectAreaCode;
}

/** Page geometry and type sizes, in PDF points. Shared with the document so the
 *  estimate and the layout cannot drift apart. */
export const LADDER_PDF_METRICS = {
  pageWidth: 842, // A4 landscape
  pageHeight: 595,
  margin: 28,
  headerHeight: 62,
  columnHeaderHeight: 20,
  /** The footer carries the page number and the area legend (up to two lines). */
  footerHeight: 26,
  nameColumnWidth: 128,
  cellPadding: 4,
  courseFontSize: 6.8,
  courseLineHeight: 8.6,
  courseGap: 2.4,
  nameLineHeight: 10.5,
  /** Average glyph width as a fraction of the font size (EB Garamond, mixed
   *  case) — rounded UP, so lines are over- rather than under-estimated. */
  glyphWidthRatio: 0.45,
  /** Kept free on every page so an estimate slightly short still fits. */
  safety: 24,
  totalsRowHeight: 22,
} as const;

export type LadderPdfMetrics = typeof LADDER_PDF_METRICS;

/** The body height available on one page, below the fixed header, the column
 *  header and the footer, minus the safety margin. */
export function bodyHeight(m: LadderPdfMetrics = LADDER_PDF_METRICS): number {
  return (
    m.pageHeight - 2 * m.margin - m.headerHeight - m.columnHeaderHeight - m.footerHeight - m.safety
  );
}

export function columnWidth(columnCount: number, m: LadderPdfMetrics = LADDER_PDF_METRICS): number {
  return (m.pageWidth - 2 * m.margin - m.nameColumnWidth) / Math.max(1, columnCount);
}

/** Estimated height of one course line: code + name wrapped to the column,
 *  the credits on the first line. */
export function courseHeight(
  course: Pick<LadderPdfCourse, 'code' | 'name'>,
  colWidth: number,
  m: LadderPdfMetrics = LADDER_PDF_METRICS,
): number {
  const usable = colWidth - 2 * m.cellPadding - 6 /* color bar */ - 16; /* credits */
  const charsPerLine = Math.max(8, Math.floor(usable / (m.courseFontSize * m.glyphWidthRatio)));
  const lines = Math.ceil((course.code.length + 1 + course.name.length) / charsPerLine);
  return lines * m.courseLineHeight + m.courseGap;
}

/** Estimated height of the program-name cell. */
function nameCellHeight(row: LadderPdfRow, m: LadderPdfMetrics): number {
  const charsPerLine = Math.floor(
    (m.nameColumnWidth - 2 * m.cellPadding) / (8.5 * m.glyphWidthRatio),
  );
  const nameLines = Math.ceil(row.name.length / charsPerLine);
  const awardLines = row.award ? 3 : 0;
  return (nameLines + 2 + awardLines) * m.nameLineHeight + 2 * m.cellPadding;
}

export function rowHeight(row: LadderPdfRow, m: LadderPdfMetrics = LADDER_PDF_METRICS): number {
  const colW = columnWidth(row.cells.length, m);
  const cells = row.cells.map((c) =>
    c.reduce((sum, course) => sum + courseHeight(course, colW, m), 0),
  );
  return Math.max(nameCellHeight(row, m), Math.max(0, ...cells) + 2 * m.cellPadding);
}

/**
 * Splits every row taller than a page into chunks that each fit (rule 2). Each
 * column is filled greedily up to the page height; a chunk always takes at least
 * one course per non-empty column, so a single course taller than a page still
 * progresses instead of looping. No course is lost or repeated.
 */
export function planLadderPdfRows(
  rows: LadderPdfRow[],
  m: LadderPdfMetrics = LADDER_PDF_METRICS,
): LadderPdfRow[] {
  const limit = bodyHeight(m) - 2 * m.cellPadding;
  const planned: LadderPdfRow[] = [];
  for (const row of rows) {
    if (rowHeight(row, m) <= bodyHeight(m)) {
      planned.push(row);
      continue;
    }
    const colW = columnWidth(row.cells.length, m);
    const next = row.cells.map(() => 0);
    let first = true;
    while (next.some((i, col) => i < row.cells[col]!.length)) {
      const cells = row.cells.map((courses, col) => {
        const taken: LadderPdfCourse[] = [];
        let used = 0;
        while (next[col]! < courses.length) {
          const course = courses[next[col]!]!;
          const h = courseHeight(course, colW, m);
          if (taken.length > 0 && used + h > limit) break;
          taken.push(course);
          used += h;
          next[col]! += 1;
        }
        return taken;
      });
      planned.push({ ...row, cells, continuation: !first });
      first = false;
    }
  }
  return planned;
}

/** Whether the totals row can travel with the last row without overflowing. */
export function canGlueTotals(
  rows: LadderPdfRow[],
  m: LadderPdfMetrics = LADDER_PDF_METRICS,
): boolean {
  const last = rows[rows.length - 1];
  return !!last && rowHeight(last, m) + m.totalsRowHeight <= bodyHeight(m);
}

/**
 * Builds the PDF data from the same ladder the screen shows. `areaLabel` names
 * a subject area for the legend; `notes` turns the ladder's observations
 * (misplaced courses, broken links) into sentences, so the PDF never hides what
 * the screen warns about.
 */
export function buildLadderPdfData(params: {
  programs: LadderProgram[];
  courses: Map<string, LadderCourseInfo>;
  tenantName: string;
  logoUrl?: string;
  labels: LadderPdfLabels;
  columnFallback: (n: number) => string;
  /** Cómo se nombra un año de la escalera. Lo pone quien llama porque el PDF
   *  tampoco traduce. */
  yearName: (n: number) => string;
  areaLabel: (area: SubjectAreaCode) => string;
  /** Legend entry for courses with no subject area yet (no CCD). */
  noAreaLabel?: string;
  notes: (ladder: ProgramLadder, nameOf: (id: string) => string) => string[];
  metrics?: LadderPdfMetrics;
}): LadderPdfData {
  const m = params.metrics ?? LADDER_PDF_METRICS;
  const ladder = buildProgramLadder(params.programs);
  const nameOf = (id: string) => params.programs.find((p) => p.id === id)?.name ?? id;
  const creditsOf = (ids: string[]) =>
    [...new Set(ids)].reduce((sum, id) => sum + (params.courses.get(id)?.credits ?? 0), 0);
  const columnName = (i: number) =>
    ladderColumnLabel(ladder.columns[i], i, {
      porAnio: params.yearName,
      porPosicion: params.columnFallback,
    });

  const course = (id: string): LadderPdfCourse => {
    const c = params.courses.get(id);
    return {
      id,
      code: c?.code ?? '?',
      name: c?.name ?? id,
      credits: c?.credits ?? 0,
      color: c?.area ? AREA_COLOR[c.area] : NO_AREA_COLOR,
    };
  };

  const rows: LadderPdfRow[] = ladder.rows.map((row) => {
    const own = row.terms.flatMap((t) => t.courseIds);
    // FILTER Y NO FIND. Por años, un programa puede poner los dos semestres del
    // mismo año en una sola columna, y `find` se quedaba con el primero: el PDF
    // habría listado menos cursos de los que suman sus propios créditos, que se
    // calculan unas líneas más arriba sobre TODOS los términos.
    const cells = ladder.columns.map((col) =>
      row.terms.filter((t) => t.column === col.index).flatMap((t) => t.courseIds.map(course)),
    );
    const last = row.startColumn + row.termCount - 1;
    const includedCourseIds = ladder.rows
      .filter((r) => row.includedProgramIds.includes(r.programId))
      .flatMap((r) => r.terms.flatMap((t) => t.courseIds));
    return {
      programId: row.programId,
      name: row.name,
      span:
        row.termCount === 1
          ? columnName(row.startColumn)
          : `${columnName(row.startColumn)} – ${columnName(last)}`,
      startColumn: row.startColumn,
      termCount: row.termCount,
      ownCredits: creditsOf(own),
      ...(row.includedProgramIds.length > 0
        ? {
            award: {
              credits: creditsOf([...includedCourseIds, ...own]),
              includes: row.includedProgramIds.map(nameOf),
            },
          }
        : {}),
      cells,
      continuation: false,
    };
  });

  const planned = planLadderPdfRows(rows, m);
  const usedAreas = [
    ...new Set(
      ladder.columns.flatMap((c) =>
        c.courseIds
          .map((id) => params.courses.get(id)?.area)
          .filter((a): a is SubjectAreaCode => !!a),
      ),
    ),
  ];

  return {
    tenantName: params.tenantName,
    ...(params.logoUrl ? { logoUrl: params.logoUrl } : {}),
    columns: ladder.columns.map((c) => columnName(c.index)),
    rows: planned,
    columnCredits: ladder.columns.map((c) => creditsOf(c.courseIds)),
    glueTotalsToLastRow: canGlueTotals(planned, m),
    // No legend at all when no course has an area yet: a legend that only says
    // "no area" explains nothing.
    legend:
      usedAreas.length === 0
        ? []
        : [
            ...usedAreas.map((area) => ({
              label: params.areaLabel(area),
              color: AREA_COLOR[area],
            })),
            ...(params.noAreaLabel &&
            ladder.columns.some((c) => c.courseIds.some((id) => !params.courses.get(id)?.area))
              ? [{ label: params.noAreaLabel, color: NO_AREA_COLOR }]
              : []),
          ],
    notes: params.notes(ladder, nameOf),
    labels: params.labels,
  };
}

type Translate = (key: string, params?: Record<string, unknown>) => string;

/** The labels, from the `sis` namespace (and `cap` for subject areas is done by
 *  the caller). Kept next to the data builder, like syllabus-pdf-labels. */
export function buildProgramLadderPdfLabels(t: Translate, generatedOn: string): LadderPdfLabels {
  return {
    title: t('programLadder.pdf.title'),
    subtitle: t('programLadder.pdf.subtitle', { date: generatedOn }),
    program: t('programLadder.pdf.program'),
    continuation: t('programLadder.pdf.continuation'),
    columnTotal: t('programLadder.pdf.columnTotal'),
    legend: t('programLadder.pdf.legend'),
    notes: t('programLadder.pdf.notes'),
    credits: (count) => t('programLadder.credits', { count }),
    creditsShort: (n) => t('programLadder.creditsShort', { n }),
    award: (count, programs) => t('programLadder.pdf.award', { count, programs }),
    page: (n, total) => t('programLadder.pdf.page', { n, total }),
  };
}

/** The ladder's observations as sentences — the same warnings the screen shows. */
export function ladderPdfNotes(
  t: Translate,
  courses: Map<string, LadderCourseInfo>,
  columnFallback: (n: number) => string,
  yearName: (n: number) => string,
): (ladder: ProgramLadder, nameOf: (id: string) => string) => string[] {
  return (ladder, nameOf) => {
    const columnName = (i: number) =>
      ladderColumnLabel(ladder.columns[i], i, { porAnio: yearName, porPosicion: columnFallback });
    return [
      // Una rama por tipo: el ternario anterior rotulaba como «ciclo» cualquier
      // problema que no fuera un predecesor ausente.
      ...ladder.problems.map((p) =>
        p.kind === 'missing_predecessor'
          ? t('programLadder.problems.missingPredecessor', { program: nameOf(p.programId) })
          : p.kind === 'mixed_year_declaration'
            ? t('programLadder.problems.mixedYear', {
                programs: p.programIds.map(nameOf).join(', '),
                count: p.programIds.length,
              })
            : t('programLadder.problems.cycle', { chain: p.programIds.map(nameOf).join(' → ') }),
      ),
      ...ladder.misplaced.map((m) => {
        const c = courses.get(m.courseId);
        return t('programLadder.pdf.misplaced', {
          course: c ? `${c.code} ${c.name}` : m.courseId,
          columns: m.columns.map(columnName).join(', '),
        });
      }),
    ];
  };
}
